import { ArrowRight, BookOpen, Cable, CheckCircle2, Database, KeyRound, MessageSquare, PlugZap, ShieldCheck, Zap } from "lucide-react";
import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Footer } from "../components/layout/Footer";
import { Navbar } from "../components/layout/Navbar";
import { useLanguage } from "../context/LanguageContext";
import { useTranslation } from "../hooks/useTranslation";
import { AnalyticsEventName, buildApiAccessPath, trackApiAccessCta, trackPromotionalFunnelEvent } from "../lib/analytics";

const Products = () => {
  const { t } = useTranslation();
  const { language } = useLanguage();

  useEffect(() => {
    trackPromotionalFunnelEvent("view_products_page", {
      surface: "products_page",
      funnel_stage: "product_discovery",
      result_status: "viewed",
    });
  }, []);

  const trackCtaClick = (eventName: AnalyticsEventName, surface: string, ctaName: string, funnelStage: string, destination: string) => {
    trackPromotionalFunnelEvent(eventName, {
      surface,
      cta_name: ctaName,
      funnel_stage: funnelStage,
      destination,
      result_status: "clicked",
    });
  };

  const trackApiAccessClick = (surface: string, ctaName: string) => {
    const destination = buildApiAccessPath(surface);
    trackApiAccessCta({ surface, ctaName, destination });
  };

  const heroProofPoints = [t("products.page.hero.proof1"), t("products.page.hero.proof2"), t("products.page.hero.proof3")];

  const productCards = [
    {
      icon: <Zap className="h-8 w-8" />,
      title: t("products.page.core.title"),
      description: t("products.page.core.description"),
      points: [t("products.page.core.point1"), t("products.page.core.point2"), t("products.page.core.point3")],
      ctaText: t("products.page.core.cta"),
      ctaHref: "/document",
      eventName: "click_view_documentation" as const,
      ctaName: "view_documentation",
      funnelStage: "documentation",
    },
    {
      icon: <Database className="h-8 w-8" />,
      title: t("products.page.connectors.title"),
      description: t("products.page.connectors.description"),
      points: [t("products.page.connectors.point1"), t("products.page.connectors.point2"), t("products.page.connectors.point3")],
      ctaText: t("products.page.connectors.cta"),
      ctaHref: "#connectors",
      eventName: "click_explore_connectors" as const,
      ctaName: "explore_connector_examples",
      funnelStage: "connector_discovery",
    },
    {
      icon: <ShieldCheck className="h-8 w-8" />,
      title: t("products.page.support.title"),
      description: t("products.page.support.description"),
      points: [t("products.page.support.point1"), t("products.page.support.point2"), t("products.page.support.point3")],
      ctaText: t("products.page.support.cta"),
      ctaHref: buildApiAccessPath("products_overview"),
      eventName: "click_api_access_cta" as const,
      ctaName: "request_api_key",
      funnelStage: "api_access_interest",
    },
  ];

  const connectorThemes = [
    { icon: <BookOpen className="h-6 w-6" />, title: t("products.page.theme.education.title"), description: t("products.page.theme.education.description") },
    { icon: <Database className="h-6 w-6" />, title: t("products.page.theme.finance.title"), description: t("products.page.theme.finance.description") },
    { icon: <Cable className="h-6 w-6" />, title: t("products.page.theme.webtools.title"), description: t("products.page.theme.webtools.description") },
    { icon: <PlugZap className="h-6 w-6" />, title: t("products.page.theme.custom.title"), description: t("products.page.theme.custom.description") },
  ];

  const journeySteps = [
    { title: t("products.page.journey.step1.title"), description: t("products.page.journey.step1.description") },
    { title: t("products.page.journey.step2.title"), description: t("products.page.journey.step2.description") },
    { title: t("products.page.journey.step3.title"), description: t("products.page.journey.step3.description") },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>{t("meta.products.title")}</title>
        <meta name="description" content={t("meta.products.description")} />
        <meta name="keywords" content={t("meta.products.keywords")} />
        <meta property="og:title" content={t("meta.products.title")} />
        <meta property="og:description" content={t("meta.products.description")} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.gugudata.ai/products" />
        <meta property="og:image" content="https://www.gugudata.ai/og.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="GuGuData.AI" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t("meta.products.title")} />
        <meta name="twitter:description" content={t("meta.products.description")} />
        <meta name="twitter:image" content="https://www.gugudata.ai/og.png" />
        <link rel="canonical" href="https://www.gugudata.ai/products" />
        <html lang={language} />
      </Helmet>

      <Navbar />
      <main className="flex-grow">
        <section className="pt-32 pb-20 md:pt-40 md:pb-28 bg-white dark:bg-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl">
              <h1 className="text-4xl md:text-6xl font-bold mb-6 text-gray-900 dark:text-white leading-tight">{t("products.page.hero.title")}</h1>
              <p className="text-xl md:text-2xl text-gray-600 dark:text-gray-300 leading-relaxed mb-10">{t("products.page.hero.subtitle")}</p>
              <ul className="grid gap-3 md:grid-cols-3 mb-10">
                {heroProofPoints.map((point) => (
                  <li key={point} className="flex items-start gap-3 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-brand-blue" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to={buildApiAccessPath("products_hero")}
                  onClick={() => trackApiAccessClick("products_hero", "request_api_access")}
                  className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium text-white bg-brand-blue rounded-full shadow-lg hover:bg-opacity-90 transition-all duration-300 hover:shadow-xl"
                >
                  {t("products.page.hero.primaryCta")}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
                <Link
                  to="/document"
                  onClick={() => trackCtaClick("click_view_documentation", "products_hero", "view_documentation", "documentation", "/document")}
                  className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium text-brand-blue bg-blue-50 dark:bg-blue-900/30 rounded-full hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-all duration-300"
                >
                  {t("products.page.hero.secondaryCta")}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-gray-50 dark:bg-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-3 gap-8">
              {productCards.map((card) => (
                <article key={card.title} className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-8 flex flex-col">
                  <div className="h-14 w-14 rounded-xl bg-blue-100 dark:bg-blue-900/30 text-brand-blue flex items-center justify-center mb-6">{card.icon}</div>
                  <h2 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-white">{card.title}</h2>
                  <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-6">{card.description}</p>
                  <ul className="space-y-3 mb-8 flex-grow">
                    {card.points.map((point) => (
                      <li key={point} className="flex items-start text-gray-600 dark:text-gray-300">
                        <CheckCircle2 className="h-5 w-5 text-brand-blue mr-3 mt-0.5 flex-shrink-0" />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                  <a
                    href={card.ctaHref}
                    onClick={() =>
                      card.eventName === "click_api_access_cta"
                        ? trackApiAccessClick("products_overview", card.ctaName)
                        : trackCtaClick(card.eventName, "products_overview", card.ctaName, card.funnelStage, card.ctaHref)
                    }
                    className="inline-flex items-center font-medium text-brand-blue hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                  >
                    {card.ctaText}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </a>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="connectors" className="py-20 bg-white dark:bg-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900 dark:text-white">{t("products.page.connectorExamples.title")}</h2>
              <p className="text-lg text-gray-600 dark:text-gray-300">{t("products.page.connectorExamples.description")}</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {connectorThemes.map((theme) => (
                <article key={theme.title} className="rounded-2xl border border-gray-200 dark:border-gray-800 p-6 bg-white dark:bg-gray-900">
                  <div className="h-12 w-12 rounded-xl bg-blue-50 dark:bg-blue-900/30 text-brand-blue flex items-center justify-center mb-5">{theme.icon}</div>
                  <h3 className="text-xl font-semibold mb-3 text-gray-900 dark:text-white">{theme.title}</h3>
                  <p className="text-gray-600 dark:text-gray-300 leading-relaxed">{theme.description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 bg-gray-50 dark:bg-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-12 items-start">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900 dark:text-white">{t("products.page.journey.title")}</h2>
                <p className="text-lg text-gray-600 dark:text-gray-300 mb-8">{t("products.page.journey.description")}</p>
                <Link
                  to={buildApiAccessPath("products_journey")}
                  onClick={() => trackApiAccessClick("products_journey", "request_api_access")}
                  className="inline-flex items-center px-6 py-3 text-base font-medium text-white bg-brand-blue rounded-lg hover:bg-opacity-90 transition-colors"
                >
                  {t("products.page.journey.cta")}
                  <KeyRound className="ml-2 h-5 w-5" />
                </Link>
              </div>
              <div className="space-y-5">
                {journeySteps.map((step, index) => (
                  <article key={step.title} className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-800">
                    <div className="flex gap-4">
                      <div className="h-10 w-10 rounded-xl bg-brand-blue text-white flex items-center justify-center font-semibold flex-shrink-0">{index + 1}</div>
                      <div>
                        <h3 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white">{step.title}</h3>
                        <p className="text-gray-600 dark:text-gray-300 leading-relaxed">{step.description}</p>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-brand-blue">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-white mb-6">{t("products.page.finalCta.title")}</h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto mb-8">{t("products.page.finalCta.description")}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                onClick={() => trackCtaClick("click_contact_sales", "products_final_cta", "contact_sales", "contact", "/contact")}
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium text-brand-blue bg-white rounded-full shadow-lg hover:bg-gray-100 transition-all duration-300"
              >
                {t("products.page.finalCta.primary")}
                <MessageSquare className="ml-2 h-5 w-5" />
              </Link>
              <Link
                to="/document"
                onClick={() => trackCtaClick("click_view_documentation", "products_final_cta", "view_documentation", "documentation", "/document")}
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium text-white border-2 border-white rounded-full hover:bg-white hover:text-brand-blue transition-all duration-300"
              >
                {t("products.page.finalCta.secondary")}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Products;
